import PropTypes from 'prop-types'
import React, { Fragment } from 'react'
import { Helmet } from 'react-helmet'
import StructuredData from '~/components/StructuredData'
import useMetadata from '~/hooks/components/use-metadata'

function Metadata({
  description,
  image,
  lang = 'en',
  meta = [],
  noindex = false,
  title,
}) {
  /*
   *  This is the order of precedence used by `useMetadata` to determine the page's metadata:
   *
   *  1. First, we look for a pageMetadata entry in Contentful with a matching path.
   *  2. Second, we look at the props passed to this component.
   *  3. Third, we look at the default pageMetadata entry in Contentful.
   */
  const { metaTitle, metaDescription, metaImage, metaUrl, structuredData } =
    useMetadata({
      description,
      image,
      title,
    })

  if (noindex) {
    meta.push({
      name: `robots`,
      content: `noindex`,
    })
  }

  return (
    <Fragment>
      <Helmet
        htmlAttributes={{
          lang,
        }}
        title={metaTitle}
        meta={[
          {
            name: `title`,
            content: metaTitle,
          },
          {
            name: `description`,
            content: metaDescription,
          },
          {
            property: `og:title`,
            content: metaTitle,
          },
          {
            property: `og:description`,
            content: metaDescription,
          },
          {
            property: `og:type`,
            content: `website`,
          },
          {
            property: `og:image`,
            content: metaImage,
          },
          {
            property: `og:url`,
            content: metaUrl,
          },
          {
            name: `twitter:card`,
            content: `summary`,
          },
          {
            name: `twitter:title`,
            content: metaTitle,
          },
          {
            name: `twitter:description`,
            content: metaDescription,
          },
          {
            name: `twitter:url`,
            content: metaUrl,
          },
        ].concat(meta)}
        link={[
          {
            rel: 'icon',
            href: '/favicon.ico',
          },
          {
            rel: 'apple-touch-icon',
            sizes: '180x180',
            href: '/apple-touch-icon.png',
          },
          {
            rel: 'icon',
            type: 'image/png',
            sizes: '32x32',
            href: '/favicon-32x32.png',
          },
          {
            rel: 'icon',
            type: 'image/png',
            sizes: '16x16',
            href: '/favicon-16x16.png',
          },
          {
            rel: 'mask-icon',
            href: '/safari-pinned-tab.svg',
            color: '#193f61',
          },
          {
            rel: 'canonical',
            href: metaUrl.replace('/en-US/', '/'),
          },
        ]}
      />
      {structuredData && (
        <StructuredData type={structuredData['@type']} data={structuredData} />
      )}
    </Fragment>
  )
}

Metadata.propTypes = {
  description: PropTypes.string,
  image: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      content: PropTypes.string,
    })
  ),
  title: PropTypes.string,
}

export default Metadata
